import type { InitialProfileStateType, NotificationType } from 'src/redux/profile/types';

export interface GroupedNotificationKey {
  senderName: string;
  workOrderId: string;
  type: string;
}

export interface GroupedNotification extends NotificationType {
  groupedCount: number;
  groupedNotificationIds: string[];
  latestMessageTimestamp: string;
}

/** Message types shown as one row per thread: same sender on the same work order (ORI-998). */
const VENDOR_THREAD_NOTIFICATION_TYPE = 'vendor.message.received';
const COORDINATOR_MESSAGE_NOTIFICATION_TYPE = 'coordinator.message.received';

// Coordinator message notifications also come from residents and technicians; only vendor threads merge, matching the server.
const isThreadNotification = (notification: NotificationType): boolean =>
  notification.type === VENDOR_THREAD_NOTIFICATION_TYPE ||
  (notification.type === COORDINATOR_MESSAGE_NOTIFICATION_TYPE &&
    notification.message?.sender_type?.toLowerCase() === 'vendor');

/**
 * Creates a unique key for grouping message notifications
 * Groups by sender name, work order ID, and notification type
 */
export const createNotificationGroupKey = (notification: NotificationType): string => {
  if (isThreadNotification(notification)) {
    const senderName = notification.message?.sender_name;
    const workOrderId = notification.work_order?.work_order_id;

    // Only create a group key if we have both sender and work order
    if (!senderName || !workOrderId) {
      return ''; // Don't group if missing critical data
    }

    return `${notification.type}:${senderName}:${workOrderId}`;
  }
  return '';
};

/**
 * Checks if a notification should be grouped
 */
export const shouldGroupNotification = (notification: NotificationType): boolean =>
  isThreadNotification(notification);

/**
 * Finds an existing notification that should be grouped with the new one
 */
export const findGroupableNotification = (
  newNotification: NotificationType,
  notificationSource: { [key: string]: NotificationType },
): string | null => {
  if (!shouldGroupNotification(newNotification)) {
    return null;
  }

  const newGroupKey = createNotificationGroupKey(newNotification);
  if (!newGroupKey) return null;

  // Find existing notification with the same group key
  const foundEntry = Object.entries(notificationSource).find(
    ([_notificationId, existingNotification]) => {
      if (shouldGroupNotification(existingNotification)) {
        const existingGroupKey = createNotificationGroupKey(existingNotification);
        return existingGroupKey === newGroupKey;
      }
      return false;
    },
  );

  if (foundEntry) {
    return foundEntry[0]; // Return the notificationId
  }

  return null;
};

/**
 * Creates a grouped notification from an existing notification and a new one
 */
export const createGroupedNotification = (
  existingNotification: NotificationType,
  newNotification: NotificationType,
): GroupedNotification => {
  // Cast to GroupedNotification to access grouped fields
  const existing = existingNotification as GroupedNotification;

  return {
    ...existingNotification,
    read_at: newNotification.read_at,
    cleared_at: newNotification.cleared_at,
    // Update timestamp to the latest message
    created_at: newNotification.created_at,
    // Increment the count
    groupedCount: (existing.groupedCount || 1) + 1,
    // Add the new notification ID to the grouped list
    groupedNotificationIds: [
      ...(existing.groupedNotificationIds || [existingNotification.notification_id]),
      newNotification.notification_id,
    ],
    // Track the latest message timestamp
    latestMessageTimestamp: newNotification.created_at,
    // Keep the latest message data for display
    message: newNotification.message,
  };
};

/**
 * Folds `incoming` into `target` when both are rows of the SAME thread (same group key).
 * `target` keeps its id and its place; member ids are united (so a message is never counted
 * twice) and the newest message's time and text are shown. The row is unread unless both
 * rows are read (each row's own `read_at` reflects its newest message). `cleared_at` is
 * taken from `incoming`.
 */
export const mergeGroupedNotifications = (
  target: NotificationType,
  incoming: NotificationType,
): GroupedNotification => {
  const targetGroup = target as GroupedNotification;
  const incomingGroup = incoming as GroupedNotification;
  const memberIds = Array.from(
    new Set([
      ...(targetGroup.groupedNotificationIds || [target.notification_id]),
      ...(incomingGroup.groupedNotificationIds || [incoming.notification_id]),
    ]),
  );
  const newest =
    new Date(incoming.created_at).getTime() > new Date(target.created_at).getTime()
      ? incoming
      : target;

  return {
    ...target,
    read_at: target.read_at && incoming.read_at ? newest.read_at : null,
    cleared_at: incoming.cleared_at,
    created_at: newest.created_at,
    message: newest.message,
    groupedCount: memberIds.length,
    groupedNotificationIds: memberIds,
    latestMessageTimestamp: newest.created_at,
  };
};

/** A `sort` request param asks for oldest-first (`created_at`, not `-created_at`). */
export const isAscendingSort = (sort: unknown): boolean =>
  typeof sort === 'string' && sort !== '' && !sort.startsWith('-');

/**
 * Put ONE row into a list that is already in the server's order, without
 * re-sorting the other rows. The row goes before the first neighbour it outranks by
 * `created_at` in the list's direction, or at the end. Used for a live (Pusher) row,
 * an un-cleared row and a newly cleared row.
 */
export const placeIdByCreatedAt = (
  ids: string[],
  id: string,
  source: { [key: string]: NotificationType },
  ascending = false,
): string[] => {
  const time = (key: string) => new Date(source[key]?.created_at || 0).getTime();
  const rest = ids.filter((other) => other !== id);
  const at = rest.findIndex((other) =>
    ascending ? time(other) > time(id) : time(other) < time(id),
  );
  return at === -1 ? [...rest, id] : [...rest.slice(0, at), id, ...rest.slice(at)];
};

/** Marks a placeholder id; never a real notification id. */
const MEMBER_SLOT_PREFIX = '\u0000member:';

/**
 * The two notifications-list slice fields that the `getNotification().fulfilled`
 * reducer and the server-rendered `/work-queues` page both produce.
 */
interface NotificationsListSlice {
  ids: InitialProfileStateType['notifications']['data'];
  source: InitialProfileStateType['notificationSource'];
}

/**
 * Build the settled `{ ids, source }` pair for the notifications list from one
 * page of rows.
 *
 * APX-522: lifted verbatim out of the `getNotification().fulfilled` case in
 * `src/redux/profile/extra-reducers.ts` so the Redux reducer and the
 * server-side read (`src/server/work-queue-rows.ts`) produce the same ids, the
 * same grouping and the same order BY CONSTRUCTION rather than by review. The
 * only substitutions made during the move are
 * `state.notifications.data` -> `existing.ids` and
 * `state.notificationSource` -> `existing.source`.
 *
 * Paginate-vs-fresh is expressed by WHAT the caller passes as `existing` — a
 * seeded pair appends, `{ ids: [], source: {} }` replaces — rather than by an
 * `isPaginating` boolean, so "page 1 reads no prior state" is explicit at every
 * call site instead of hidden in a flag.
 *
 * IMMER CONTRACT — when called from the reducer, `existing.source` holds Immer
 * DRAFT proxies of the current slice. This function only READS them and re-packs
 * them into fresh containers, which the reducer assigns straight back to
 * `state`, where Immer finalizes them. That is valid ONLY when the call is
 * SYNCHRONOUS and INSIDE the producer. Never `await` around this call, never
 * cache or defer the returned `source`, and never hand it to anything that
 * outlives the reducer: Immer revokes the proxies when the producer returns and
 * every later read would throw.
 */
export const buildNotificationsListState = (
  rows: NotificationType[],
  existing: NotificationsListSlice,
): NotificationsListSlice => {
  // The shallow copies stay INSIDE this function — deliberately NOT hoisted to
  // the call site — so the reducer keeps passing the draft and this code keeps
  // spreading it exactly as the previously-inline block did. That is what makes
  // the extraction behaviour-preserving under Immer.
  const newSource: NotificationsListSlice['source'] = { ...existing.source };
  const newIds: NotificationsListSlice['ids'] = [...existing.ids];

  // Group map to track vendor messages from new page only
  const groupMap = new Map<string, NotificationType[]>();
  // The list keeps the SERVER's order (it owns the sort, including
  // `sort=created_at` and the default newest-first). Each group member reserves a
  // slot where it appeared in the page; the second pass puts the group's row in its
  // NEWEST member's slot (the time the row shows) and drops the other slots.
  const memberSlot = (notificationId: string) => `${MEMBER_SLOT_PREFIX}${notificationId}`;

  // First pass: collect all NEW notifications and group vendor messages
  rows.forEach((item: NotificationType) => {
    if (shouldGroupNotification(item)) {
      const groupKey = createNotificationGroupKey(item);
      if (groupKey) {
        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, []);
        }
        groupMap.get(groupKey)?.push(item);
        newIds.push(memberSlot(item.notification_id));
      } else {
        // Can't group (missing sender/work_order), add normally
        if (item?.notification_id && newIds?.includes(item?.notification_id)) {
          newIds.splice(newIds.indexOf(item?.notification_id), 1);
        }
        newIds.push(item?.notification_id);
        newSource[item?.notification_id] = item;
      }
    } else {
      // Non-vendor message notification, add normally
      if (item?.notification_id && newIds?.includes(item?.notification_id)) {
        newIds.splice(newIds.indexOf(item?.notification_id), 1);
      }
      newIds.push(item?.notification_id);
      newSource[item?.notification_id] = item;
    }
  });

  // Second pass: process grouped vendor messages
  groupMap.forEach((notifications) => {
    if (notifications.length > 0) {
      // Sort by created_at to get the latest message
      const sortedNotifications = notifications.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      // Use the first (latest) notification as the main one
      const mainNotification = sortedNotifications[0];
      const groupedNotificationIds = notifications.map((n) => n.notification_id);

      // Create grouped notification
      const groupedNotification = {
        ...mainNotification,
        groupedCount: notifications.length,
        groupedNotificationIds,
        latestMessageTimestamp: mainNotification.created_at,
      };

      // A thread whose earlier messages came in on an earlier page already has a row:
      // fold this page's messages into it instead of adding a second row (ORI-998).
      const existingRowId = findGroupableNotification(mainNotification, newSource);
      const existingRow = existingRowId ? newSource[existingRowId] : undefined;
      if (existingRowId && existingRow) {
        const mergedNotification = mergeGroupedNotifications(existingRow, groupedNotification);
        newSource[existingRowId] = mergedNotification;
        // The row sits where its NEWEST message is: if this page holds it, the row moves to
        // that slot; otherwise it stays where it was and this page's slots are dropped.
        // `>=`: a live reply already gave the row this time, and its own page must still move it.
        const movesToThisPage =
          new Date(mainNotification.created_at).getTime() >=
          new Date(existingRow.created_at).getTime();
        if (movesToThisPage) {
          const existingIndex = newIds.indexOf(existingRowId);
          if (existingIndex !== -1) {
            newIds.splice(existingIndex, 1);
          }
          newIds[newIds.indexOf(memberSlot(mainNotification.notification_id))] = existingRowId;
        }
        return;
      }

      // No row of this thread yet: fill the group's slot. An earlier copy of the main id stored
      // without a group key moves here, like a repeated ungrouped id does in the first pass.
      const staleIndex = newIds.indexOf(mainNotification.notification_id);
      if (staleIndex !== -1) {
        newIds.splice(staleIndex, 1);
      }
      newIds[newIds.indexOf(memberSlot(mainNotification.notification_id))] =
        mainNotification.notification_id;
      newSource[mainNotification.notification_id] = groupedNotification;
    }
  });

  return { ids: newIds.filter((id) => !id.startsWith(MEMBER_SLOT_PREFIX)), source: newSource };
};

/**
 * Gets the display text for grouped notifications
 */
export const getGroupedNotificationText = (notification: GroupedNotification): string => {
  const senderName = notification.message?.sender_name || 'Unknown';
  const count = notification.groupedCount || 1;

  if (count > 1) {
    return `${count} new messages from ${senderName}`;
  }
  return `New message from ${senderName}`;
};
